import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { XIcon } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice"
import { useFullscreenHost } from "@vc/viewmodels/screen/useFullscreenHost"

// Step 1: Context for size
type DialogSize = "xs" | "sm" | "md" | "lg" | "xl" | "full"

const DialogContext = React.createContext<{ size: DialogSize }>({ size: "md" })

// Step 2: Match MUI Dialog maxWidth values
const sizeClasses: Record<DialogSize, string> = {
  xs: "max-w-[360px]",
  sm: "max-w-[600px]",
  md: "max-w-[960px]",
  lg: "max-w-[1280px]",
  xl: "max-w-[1920px]",
  full: "w-full max-w-full"
}

interface DialogProps extends React.ComponentProps<typeof DialogPrimitive.Root> {
  size?: DialogSize
}

function Dialog({ size = "md", ...props }: DialogProps) {
  return (
    <DialogContext.Provider value={{ size }}>
      <DialogPrimitive.Root data-slot="dialog" {...props} />
    </DialogContext.Provider>
  )
}

function DialogTrigger(
  props: React.ComponentProps<typeof DialogPrimitive.Trigger>
) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal(
  props: React.ComponentProps<typeof DialogPrimitive.Portal>
) {
  // Anchored to whatever owns the fullscreen layer, not always `document.body`.
  //
  // A fullscreened element is the only thing the browser paints. While a shared
  // screen, shared video, document or maximised tile holds that layer, a dialog
  // portalled to the body is rendered somewhere off screen — so every shortcut
  // that opens one (`?`, Adjust view, Settings, Leave) appeared to do nothing.
  //
  // A no-op outside fullscreen and in whole-page fullscreen, where the resolved
  // host IS the body: same container as before, same behaviour.
  const host = useFullscreenHost()
  return (
    <DialogPrimitive.Portal
      data-slot="dialog-portal"
      {...props}
      container={props.container ?? host ?? undefined}
    />
  )
}

function DialogClose(
  props: React.ComponentProps<typeof DialogPrimitive.Close>
) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80",
        className
      )}
      {...props}
    />
  )
}

function DialogContent({
  className,
  children,
  maxWidthClass, // NEW: Custom max-width for desktop only
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  maxWidthClass?: string
}) {
  const { size } = React.useContext(DialogContext)
  const isFull = size === "full"
  const { shouldUseMobileUI, isMobileDevice, isLandscape } = useResponsiveDevice()
  
  // Phone landscape: side panel from right
  const isPhoneLandscape = isMobileDevice && isLandscape
  // Phone portrait: bottom sheet
  const isPhonePortrait = isMobileDevice && !isLandscape

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          "bg-pannelcolor fixed z-50 grid gap-4 overflow-x-hidden border border-stroke-500 shadow-lg duration-200",
          "data-[state=open]:animate-in data-[state=closed]:animate-out",
          "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
          "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
          className,
          // Phone portrait: bottom sheet style
          isPhonePortrait && [
            "left-0 right-0 bottom-0 w-full max-w-none max-h-[95vh] rounded-t-2xl p-4",
            "rounded-none rounded-t-2xl",
            "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom"
          ],
          // Phone landscape: side panel from right
          isPhoneLandscape && [
            "top-0 right-0 h-full w-[90vw] max-w-[420px] p-4",
            "rounded-none",
            "data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right"
          ],
          // Tablet/Desktop: centered modal dialog
          // Custom maxWidthClass applies here, overriding sizeClasses if provided
          !shouldUseMobileUI && [
            "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-lg p-4",
            isFull ? "w-full" : "w-auto w-full",
            maxWidthClass || sizeClasses[size]
          ]
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close
          className={cn(
            "absolute flex justify-center items-center hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full w-8 h-8 opacity-70 transition-opacity hover:opacity-100 focus:ring-ring focus:ring-offset-2 data-[state=open]:bg-bg-100 data-[state=open]:text-text-500 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 cursor-pointer",
            shouldUseMobileUI ? "top-3 right-3" : "top-4 right-4"
          )}
        >
          <XIcon className="cursor-pointer" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  )
}

function DialogHeader({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn("flex flex-col gap-2 border-b pb-2 text-center sm:text-left", className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end", className)}
      {...props}
    />
  )
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn("text-lg font-semibold leading-none", className)}
      {...props}
    />
  )
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-sm text-muted-foreground", className)}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogClose,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
